// 연동 가능 기기 목록 (최대 MAX_DEVICE_COUNT 개까지 선택 가능)
import api from "./api";

export const MAX_DEVICE_COUNT = 3;

export type DeviceType = 'pressure' | 'weight' | 'stethoscope' | 'blood' | 'ecg';

export type DeviceInfo = {
  id: string,          // 기기 식별 코드
  type: DeviceType,    // 기기 분류
  typeName: string,    // 기기 분류명
  name: string,        // 기기명
  excluded: boolean,   // 실증 제외 여부
}

export const DEVICE_LIST: DeviceInfo[] = [
  { id: 'BP170', type: 'pressure', typeName: '혈압계', name: 'InBody BP170', excluded: false },
  { id: 'MD6280', type: 'pressure', typeName: '혈압계', name: 'InBody MD6280', excluded: false },
  { id: 'T9', type: 'weight', typeName: '체중계', name: 'ATFLEE T9', excluded: false },
  { id: 'IGRIPX', type: 'weight', typeName: '체중계', name: '아이그립X', excluded: false },
  { id: 'H10', type: 'stethoscope', typeName: '청진기', name: 'Polar H10', excluded: true },
  { id: 'CSNP', type: 'blood', typeName: '혈당기', name: 'CareSens N Premier', excluded: false },
  { id: 'U8', type: 'ecg', typeName: 'ECG', name: 're:fit patch U8', excluded: false },
];

// 기기 분류 순서 (화면 노출 순서)
export const DEVICE_TYPE_ORDER: DeviceType[] = ['pressure', 'weight', 'stethoscope', 'blood', 'ecg'];

// 분류별로 묶은 기기 목록
export function getDeviceListByType() {
  return DEVICE_TYPE_ORDER.map((type) => ({
    type: type,
    typeName: DEVICE_LIST.filter((v) => v.type === type)[0].typeName,
    list: DEVICE_LIST.filter((v) => v.type === type),
  }));
}

export function getDeviceById(id: string) {
  return DEVICE_LIST.filter((v) => v.id === id)[0];
}

export function getDeviceListByIds(ids: string[]) {
  return ids.map((id) => getDeviceById(id)).filter((v) => v !== undefined);
}


// ─────────────────────────────────────────────────────────────
// 기기별 측정 항목 / 측정 데이터
// TODO: 연동 API 확정 시 아래 MOCK_* 를 실제 조회 결과로 교체할 것
// ─────────────────────────────────────────────────────────────

// 측정 항목 그룹 (기기별 데이터 정의서 상단 헤더 기준)
export type MetricGroup = 'pressure' | 'body' | 'hr' | 'glucose';

export const METRIC_GROUP_NAME: { [key in MetricGroup]: string } = {
  pressure: '혈압',
  body: '체중 체성분',
  hr: '심박',
  glucose: '혈당',
};

export type DeviceMetric = {
  key: string,          // 측정 항목 코드 (연동 데이터 컬럼명)
  label: string,        // 측정 항목명
  unit: string,         // 단위
  group: MetricGroup,   // 항목 그룹
  min?: number,         // 게이지 최소값
  max?: number,         // 게이지 최대값
  subject?: string,     // 안내 문구에 쓰는 이름 (예: 혈압이 높아요.)
  normalMin?: number,   // 정상 범위 최소값
  normalMax?: number,   // 정상 범위 최대값
}

// 전체 측정 항목 정의
const METRIC_DEF: { [key: string]: DeviceMetric } = {
  systolic:       { key: 'systolic', label: '수축기', unit: 'mmHg', group: 'pressure', min: 80, max: 180, subject: '혈압', normalMin: 90, normalMax: 120 },
  diastolic:      { key: 'diastolic', label: '이완기', unit: 'mmHg', group: 'pressure', min: 40, max: 120, subject: '혈압', normalMin: 61, normalMax: 80 },
  pulse:          { key: 'pulse', label: '맥박', unit: 'bpm', group: 'pressure', min: 40, max: 140, subject: '맥박', normalMin: 60, normalMax: 100 },

  weight_kg:      { key: 'weight_kg', label: '체중', unit: 'kg', group: 'body', min: 30, max: 150, subject: '체중' },
  body_fat_pct:   { key: 'body_fat_pct', label: '체지방률', unit: '%', group: 'body', min: 5, max: 50, subject: '체지방률', normalMin: 10, normalMax: 25 },
  ffm_kg:         { key: 'ffm_kg', label: '제지방량', unit: 'kg', group: 'body', min: 20, max: 90, subject: '제지방량' },
  smm_kg:         { key: 'smm_kg', label: '골격근량', unit: 'kg', group: 'body', min: 10, max: 60, subject: '골격근량' },
  tbw_l:          { key: 'tbw_l', label: '체수분', unit: 'L', group: 'body', min: 15, max: 60, subject: '체수분' },
  bmr_kcal:       { key: 'bmr_kcal', label: '기초대사량', unit: 'kcal', group: 'body', min: 800, max: 2500, subject: '기초대사량' },
  bmi:            { key: 'bmi', label: 'BMI', unit: '', group: 'body', min: 10, max: 40, subject: '체중', normalMin: 18.5, normalMax: 22.9 },

  avg_hr:         { key: 'avg_hr', label: '평균 심박', unit: 'bpm', group: 'hr', min: 40, max: 140, subject: '심박', normalMin: 60, normalMax: 100 },
  min_hr:         { key: 'min_hr', label: '최저 심박', unit: 'bpm', group: 'hr', min: 30, max: 120, subject: '심박' },
  max_hr:         { key: 'max_hr', label: '최고 심박', unit: 'bpm', group: 'hr', min: 60, max: 200, subject: '심박' },

  glucose_mg_dl:  { key: 'glucose_mg_dl', label: '혈당', unit: 'mg/dL', group: 'glucose', min: 50, max: 250, subject: '혈당', normalMin: 70, normalMax: 99 },
};

// 기기별 측정 항목 (기기별 데이터 정의서 매트릭스)
const DEVICE_METRIC_KEYS: { [deviceId: string]: string[] } = {
  BP170:  ['systolic', 'diastolic', 'pulse'],
  MD6280: ['systolic', 'diastolic', 'pulse'],
  T9:     ['weight_kg', 'body_fat_pct', 'ffm_kg', 'smm_kg', 'tbw_l', 'bmr_kcal', 'bmi'],
  IGRIPX: ['weight_kg', 'body_fat_pct', 'ffm_kg', 'smm_kg', 'tbw_l', 'bmr_kcal', 'bmi'],
  H10:    ['avg_hr', 'min_hr', 'max_hr'],
  CSNP:   ['glucose_mg_dl'],
  // ECG 의 부정맥 / 스트레스 지수는 수치 항목이 아니라 전용 영역에서 노출
  U8:     ['avg_hr', 'min_hr', 'max_hr'],
};

// 기기별 대표 항목 (게이지 / 추이 그래프)
const DEVICE_PRIMARY_KEY: { [deviceId: string]: string } = {
  BP170:  'systolic',
  MD6280: 'systolic',
  T9:     'weight_kg',
  IGRIPX: 'weight_kg',
  H10:    'avg_hr',
  CSNP:   'glucose_mg_dl',
  U8:     'avg_hr',
};

export function getDeviceMetrics(deviceId: string) {
  return (DEVICE_METRIC_KEYS[deviceId] || []).map((key) => METRIC_DEF[key]);
}

export function getPrimaryMetric(deviceId: string) {
  return METRIC_DEF[DEVICE_PRIMARY_KEY[deviceId]] || getDeviceMetrics(deviceId)[0];
}

// 측정 항목을 그룹 단위로 묶은 목록
export function getDeviceMetricGroups(deviceId: string) {
  const metrics = getDeviceMetrics(deviceId);
  const groups: MetricGroup[] = [];
  for (let metric of metrics) {
    if (groups.indexOf(metric.group) === -1) {
      groups.push(metric.group);
    }
  }
  return groups.map((group) => ({
    group: group,
    groupName: METRIC_GROUP_NAME[group],
    list: metrics.filter((v) => v.group === group),
  }));
}

// 게이지 노출용 0~100 환산값
export function getGaugeValue(metric: DeviceMetric, value: number) {
  const min = metric.min === undefined ? 0 : metric.min;
  const max = metric.max === undefined ? 100 : metric.max;
  if (max <= min) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round((value - min) / (max - min) * 100)));
}

// 요약 영역에 함께 노출할 항목 (미지정 시 대표 항목 하나만 노출)
const DEVICE_SUMMARY_KEYS: { [deviceId: string]: string[] } = {
  BP170: ['systolic', 'diastolic'],
  MD6280: ['systolic', 'diastolic'],
};

// 카드 / 상세 상단에 크게 노출할 값
export function getSummaryValue(deviceId: string, latest: { [key: string]: number }) {
  const keys = DEVICE_SUMMARY_KEYS[deviceId];
  if (keys === undefined) {
    const metric = getPrimaryMetric(deviceId);
    return { text: String(latest[metric.key]), unit: metric.unit };
  }
  return {
    text: keys.map((key) => latest[key]).join('/'),
    unit: METRIC_DEF[keys[0]].unit,
  };
}

// 상태 문구 판정에 사용할 항목 (미지정 시 대표 항목)
const DEVICE_STATUS_KEY: { [deviceId: string]: string } = {
  T9: 'bmi',
  IGRIPX: 'bmi',
};

export function getStatusMetric(deviceId: string) {
  return METRIC_DEF[DEVICE_STATUS_KEY[deviceId]] || getPrimaryMetric(deviceId);
}

// 한글 목적격 조사 (을/를)
function getObjectParticle(word: string) {
  return hasFinalConsonant(word) ? '을' : '를';
}

// 한글 주격 조사 (이/가)
function getSubjectParticle(word: string) {
  return hasFinalConsonant(word) ? '이' : '가';
}

function hasFinalConsonant(word: string) {
  if (word === '') {
    return false;
  }
  const code = word.charCodeAt(word.length - 1);
  if (code < 0xAC00 || code > 0xD7A3) {
    return false;
  }
  return (code - 0xAC00) % 28 !== 0;
}

// 화면 하단 정상범위 안내 (기기 분류별)
// 실제 상태 판정 기준과 같은 값을 적어둘 것 (getMetricStatus / getPressureStatus 참고)
export const NORMAL_RANGE_GUIDE: { [key in DeviceType]: { title: string, lines: string[] } } = {
  pressure: {
    title: '혈압정상범위',
    lines: [
      '- 수축기 101~120mmHg - 이완기 61~80mmHg',
      '- 맥박 60~100bpm',
    ],
  },
  weight: {
    title: '체성분표준범위',
    lines: [
      '- BMI 18.5~22.9 - 체지방률 남 10~20% / 여 18~28%',
      '- 체중 · 제지방량 · 골격근량 · 체수분 · 기초대사량은 키와 성별을 기준으로 계산한 표준 범위',
    ],
  },
  stethoscope: {
    title: '심박정상범위',
    lines: [
      '- 안정 시 심박 60~100bpm',
    ],
  },
  blood: {
    title: '혈당정상범위',
    lines: [
      '- 식전 70~100mg/dL - 식후(2시간) 90~140mg/dL',
      '- 취침전 100~150mg/dL',
    ],
  },
  ecg: {
    title: '심박 / 스트레스 정상범위',
    lines: [
      '- 안정 시 심박 60~100bpm',
      '- 스트레스 지수 0~40 (1~2단계)',
      '- 부정맥은 이상이 확인된 기록만 표시',
    ],
  },
};

// ─────────────────────────────────────────────────────────────
// 체성분 표준 범위 - 회원의 키 / 성별을 기준으로 산출
//   체중     : BMI 18.5~22.9 환산 (LineChart 의 정상 체중 기준선과 동일한 계산)
//   체지방률  : 남 10~20% / 여 18~28%
//   제지방량  : 표준 체중 × (1 - 표준 체지방률)
//   골격근량  : 제지방량 × 0.52
//   체수분    : 제지방량 × 0.73
//   기초대사량 : 370 + 21.6 × 제지방량 (Katch-McArdle 공식)
// ─────────────────────────────────────────────────────────────

export type MetricRange = { min: number, max: number };

const BMI_NORMAL_MIN = 18.5;
const BMI_NORMAL_MAX = 22.9;

// 성별 표준 체지방률
const BODY_FAT_NORMAL = { male: { min: 10, max: 20 }, female: { min: 18, max: 28 } };

const SMM_RATE = 0.52;   // 골격근량 / 제지방량
const TBW_RATE = 0.73;   // 체수분 / 제지방량
const BMR_BASE = 370;    // 기초대사량 기본값
const BMR_PER_KG = 21.6; // 제지방량 1kg 당 기초대사량

// 가입 시 성별은 남성이 true 로 저장됨
// TODO: 연동 API 확정 시 프로필 응답의 성별 필드명 / 형식 확인할 것
function isMaleProfile(sex: any) {
  return sex === true || sex === 1 || sex === '1' || sex === 'M';
}

function roundTo1(value: number) {
  return Math.round(value * 10) / 10;
}

// 키 정보가 없으면 빈 값을 반환해 항목별 고정 정상 범위를 그대로 쓰게 함
export function getBodyStandardRanges(height: number, sex: any) {
  const ranges: { [key: string]: MetricRange } = {};
  if (!(height > 0)) {
    return ranges;
  }

  const heightM = height / 100;
  const weight = {
    min: roundTo1(BMI_NORMAL_MIN * heightM * heightM),
    max: roundTo1(BMI_NORMAL_MAX * heightM * heightM),
  };
  const bodyFat = isMaleProfile(sex) ? BODY_FAT_NORMAL.male : BODY_FAT_NORMAL.female;

  // 표준 체지방률의 가운데 값을 뺀 나머지가 표준 제지방량
  const leanRate = 1 - (bodyFat.min + bodyFat.max) / 2 / 100;
  const ffm = { min: roundTo1(weight.min * leanRate), max: roundTo1(weight.max * leanRate) };

  ranges.weight_kg = weight;
  ranges.body_fat_pct = bodyFat;
  ranges.ffm_kg = ffm;
  ranges.smm_kg = { min: roundTo1(ffm.min * SMM_RATE), max: roundTo1(ffm.max * SMM_RATE) };
  ranges.tbw_l = { min: roundTo1(ffm.min * TBW_RATE), max: roundTo1(ffm.max * TBW_RATE) };
  ranges.bmr_kcal = {
    min: Math.round(BMR_BASE + BMR_PER_KG * ffm.min),
    max: Math.round(BMR_BASE + BMR_PER_KG * ffm.max),
  };
  ranges.bmi = { min: BMI_NORMAL_MIN, max: BMI_NORMAL_MAX };

  return ranges;
}

// 체성분 항목은 높낮이 대신 표준 기준으로 표기 (혈압 / 혈당 등은 기존 표기 유지)
const BODY_STATUS_TEXT: { [key: string]: string } = {
  '낮음': '표준이하',
  '정상': '표준',
  '높음': '표준이상',
};

export function getMetricStatusText(metric: DeviceMetric, status: MetricStatus) {
  if (metric.group === 'body' && BODY_STATUS_TEXT[status.text] !== undefined) {
    return BODY_STATUS_TEXT[status.text];
  }
  return status.text;
}

// 3구간 차트에 쓰는 구간 이름 (낮은 쪽 → 높은 쪽)
export function getMetricRangeLabels(metric: DeviceMetric) {
  return ['낮음', '정상', '높음'].map((text) => (
    metric.group === 'body' && BODY_STATUS_TEXT[text] !== undefined ? BODY_STATUS_TEXT[text] : text
  ));
}

export type MetricStatus = {
  text: string,     // 게이지 아래 상태 (없음 / 정상 / 높음 / 낮음)
  color: string,
  message: string,  // 카드 하단 안내 문구
}

// 측정값에 따른 상태 / 안내 문구
export function getMetricStatus(metric: DeviceMetric, value: number): MetricStatus {
  const subject = metric.subject || metric.label;

  if (value === undefined || value === null || value === 0) {
    return { text: '없음', color: '#777', message: '오늘 ' + subject + getObjectParticle(subject) + ' 측정하지 않으셨습니다.' };
  }
  if (metric.normalMax !== undefined && value > metric.normalMax) {
    return { text: '높음', color: '#df0000', message: subject + getSubjectParticle(subject) + ' 높아요.' };
  }
  if (metric.normalMin !== undefined && value < metric.normalMin) {
    return { text: '낮음', color: '#fba400', message: subject + getSubjectParticle(subject) + ' 낮아요.' };
  }
  return { text: '정상', color: '#279700', message: '오늘 ' + subject + ' 측정 완료!' };
}

// ─────────────────────────────────────────────────────────────
// 일자별 목데이터 (일주일치)
// 배열 index 0 이 기준일(오늘), 1 이 하루 전 ... 6 이 6일 전
// 화면의 날짜 이동값(dayValue) 은 0 = 오늘 / -1 = 어제 이므로 getDayIndex 로 변환해서 사용
// 일주일보다 오래된 날짜는 같은 주 데이터를 순환해서 노출
// ─────────────────────────────────────────────────────────────

export const MOCK_DAY_COUNT = 7;

function getDayIndex(dayOffset?: number) {
  const back = Math.abs(Math.round(dayOffset || 0));
  return back % MOCK_DAY_COUNT;
}

// 월간처럼 일주일을 넘는 구간은 같은 주 데이터가 반복되므로 주차별로 소폭 변동을 줌
// (첫 주는 변동 0 이라 일별 화면 값과 그대로 일치)
function getWeekCycleDelta(back: number, step: number) {
  return (Math.floor(back / MOCK_DAY_COUNT) % 3) * step;
}




/* 2026-08-11 수정
 * ============================================================
 * API 호출 후 위의 기존 목데이터를 실제 데이터로 덮어쓴다.
 *
 * targetDate : API 조회 날짜
 * dayValue   : 기존 MOCK 배열에서 사용할 index

 * ============================================================
 */
export async function getUserData(
    targetDate: string,
    dayValue: number
): Promise<void> {

  try {

    const res = await api.get(`app/api/v1/AP_009_004`, {
      params: {
        date: targetDate
      }
    });

    const data = res.data.data;


    /*
     * ========================================================
     * 체중계
     * ========================================================
     *
     * API
     *
     * weight.label = "T9"
     * weight.latest = {
     *   weight_kg,
     *   body_fat_pct,
     *   ffm_kg,
     *   ...
     * }
     */
    if (
        data.weight?.label &&
        data.weight?.latest
    ) {

      const deviceId = data.weight.label;
      const item = data.weight.latest;


      // 등록되지 않은 기기 ID가 내려오는 경우 대비
      if (!MOCK_LATEST_BY_DAY[deviceId]) {
        MOCK_LATEST_BY_DAY[deviceId] = [];
      }


      // ★ 해당 날짜 위치를 API 데이터로 교체
      MOCK_LATEST_BY_DAY[deviceId][dayValue] = {
        weight_kg: item.weight_kg,
        body_fat_pct: item.body_fat_pct,
        ffm_kg: item.ffm_kg,
        smm_kg: item.smm_kg,
        tbw_l: item.tbw_l,
        bmr_kcal: item.bmr_kcal,
        bmi: item.bmi,
      };
    }



    /*
     * ========================================================
     * 혈압계
     * ========================================================
     *
     * pressure.today = 선택한 날짜의 전체 측정 목록
     *
     * API는 최신 → 과거 순이므로
     * 기존 목데이터와 동일하게 과거 → 최신 순으로 reverse
     */
    if (data.pressure?.label) {

      const deviceId = data.pressure.label;


      if (!MOCK_READINGS_BY_DAY[deviceId]) {
        MOCK_READINGS_BY_DAY[deviceId] = [];
      }


      MOCK_READINGS_BY_DAY[deviceId][dayValue] =
          [...(data.pressure.today ?? [])]
              .reverse()
              .map((item: any) => ({

                time:
                    item.received_time?.substring(0, 5) ?? '',

                values: {
                  systolic: item.systolic,
                  diastolic: item.diastolic,
                  pulse: item.pulse,
                }

              }));


      /*
       * getMockLatest()에서도 혈압 최신값을 사용하는 경우를 위해
       * MOCK_LATEST_BY_DAY에도 넣어준다.
       *
       * API의 today[0] = 가장 최신 기록
       */
      if (data.pressure.today?.length > 0) {

        const item = data.pressure.today[0];


        if (!MOCK_LATEST_BY_DAY[deviceId]) {
          MOCK_LATEST_BY_DAY[deviceId] = [];
        }


        MOCK_LATEST_BY_DAY[deviceId][dayValue] = {
          systolic: item.systolic,
          diastolic: item.diastolic,
          pulse: item.pulse,
        };
      }
    }



    /*
     * ========================================================
     * 혈당계
     * ========================================================
     */
    if (data.blood?.label) {

      /*
       * 최종적으로 MOCK_BLOOD_BY_DAY[dayValue]에 들어갈 값
       *
       * 예:
       * {
       *   breakfast_pre: 108,
       *   lunch_pre: 135,
       *   lunch_post: 137,
       *   dinner_pre: 350,
       *   dinner_post: 256
       * }
       */
      const bloodData: { [key: string]: number } = {};


      /*
       * API latestDay는 최신순
       *
       * 같은 meal_tag가 여러 개 있으면
       * 첫 번째 값 = 가장 최신 측정값을 사용
       */
      for (const item of (data.blood.latestDay ?? [])) {

        const mealTag = item.meal_tag;

        /*
         * 우리가 화면에서 사용하는 7개 타입만 허용
         *
         * API에서 other 같은 값이 내려오는 경우 제외
         */
        if (
            mealTag === 'breakfast_pre' ||
            mealTag === 'breakfast_post' ||
            mealTag === 'lunch_pre' ||
            mealTag === 'lunch_post' ||
            mealTag === 'dinner_pre' ||
            mealTag === 'dinner_post' ||
            mealTag === 'sleep'
        ) {

          /*
           * 이미 값이 들어있지 않을 때만 저장
           *
           * latestDay가 최신순이기 때문에
           * 같은 meal_tag 중 가장 최신값만 남게 됨
           */
          if (bloodData[mealTag] === undefined) {

            bloodData[mealTag] =
                item.glucose_mg_dl;
          }
        }
      }


      /*
       * ★★★★★ 핵심
       *
       * 기존 혈당 목데이터의 해당 날짜를
       * 실제 API 데이터로 교체
       */
      MOCK_BLOOD_BY_DAY[dayValue] = bloodData;

      console.log(MOCK_BLOOD_BY_DAY);


      /*
       * 하루 대표 혈당값도 기존 MOCK_LATEST_BY_DAY에서
       * 사용한다면 이것은 유지
       */
      if (data.blood.latestDay?.length > 0) {

        const deviceId = data.blood.label;

        /*
         * latestDay[0] = 해당 날짜 가장 최신 혈당
         */
        const item = data.blood.latestDay[0];


        if (!MOCK_LATEST_BY_DAY[deviceId]) {
          MOCK_LATEST_BY_DAY[deviceId] = [];
        }


        MOCK_LATEST_BY_DAY[deviceId][dayValue] = {
          glucose_mg_dl: item.glucose_mg_dl,
        };
      }
    }



    /*
     * ========================================================
     * ECG
     * ========================================================
     */
    if (data.ecg?.label) {

      const deviceId = data.ecg.label;


      if (!MOCK_READINGS_BY_DAY[deviceId]) {
        MOCK_READINGS_BY_DAY[deviceId] = [];
      }


      MOCK_READINGS_BY_DAY[deviceId][dayValue] =
          [...(data.ecg.ecgList ?? [])]
              .reverse()
              .map((item: any) => ({

                time:
                    item.received_time?.substring(0, 5)
                    ??
                    item.receivedTime?.substring(0, 5)
                    ??
                    '',

                values: {
                  avg_hr: item.avg_hr ?? item.avgHr,
                  min_hr: item.min_hr ?? item.minHr,
                  max_hr: item.max_hr ?? item.maxHr,
                }

              }));


      /*
       * ECG 최신값
       */
      if (data.ecg.ecgList?.length > 0) {

        const item = data.ecg.ecgList[0];


        if (!MOCK_LATEST_BY_DAY[deviceId]) {
          MOCK_LATEST_BY_DAY[deviceId] = [];
        }


        MOCK_LATEST_BY_DAY[deviceId][dayValue] = {
          avg_hr: item.avg_hr ?? item.avgHr,
          min_hr: item.min_hr ?? item.minHr,
          max_hr: item.max_hr ?? item.maxHr,
        };
      }
    }


    console.log(
        'API 데이터 → 기존 MOCK 데이터 교체 완료',
        targetDate,
        dayValue
    );

  } catch (error) {

    console.log(error);

  }
}






// ─────────────────────────────────────────────────────────────
// 하루 여러 번 기록되는 기기 (혈압계 / ECG 심박)
// 측정시간 오름차순으로 보관하며, 마지막 항목이 그날의 최신 기록
// ─────────────────────────────────────────────────────────────

export type DeviceReading = {
  time: string,                       // 측정 시각 (HH:mm)
  values: { [key: string]: number },  // 측정 항목별 값
}

const MOCK_READINGS_BY_DAY: { [deviceId: string]: DeviceReading[][] } = {
  BP170: [
    // 오늘 - 정상
    [
      { time: '07:20', values: { systolic: 126, diastolic: 82, pulse: 74 } },
      { time: '13:05', values: { systolic: 121, diastolic: 79, pulse: 71 } },
      { time: '21:40', values: { systolic: 118, diastolic: 76, pulse: 68 } },
    ],
    // 1일 전 - 높음
    [
      { time: '07:05', values: { systolic: 134, diastolic: 88, pulse: 80 } },
      { time: '21:10', values: { systolic: 129, diastolic: 85, pulse: 76 } },
    ],
    // 2일 전 - 정상
    [
      { time: '06:55', values: { systolic: 119, diastolic: 78, pulse: 70 } },
      { time: '12:40', values: { systolic: 116, diastolic: 75, pulse: 67 } },
      { time: '22:00', values: { systolic: 114, diastolic: 74, pulse: 66 } },
    ],
    // 3일 전 - 1건만 측정 / 높음
    [
      { time: '08:00', values: { systolic: 141, diastolic: 92, pulse: 84 } },
    ],
    // 4일 전 - 정상
    [
      { time: '07:30', values: { systolic: 122, diastolic: 80, pulse: 72 } },
      { time: '20:50', values: { systolic: 117, diastolic: 77, pulse: 69 } },
    ],
    // 5일 전 - 낮음
    [
      { time: '07:10', values: { systolic: 104, diastolic: 68, pulse: 62 } },
      { time: '21:35', values: { systolic: 98, diastolic: 64, pulse: 58 } },
    ],
    // 6일 전 - 4건 측정 / 정상
    [
      { time: '06:45', values: { systolic: 128, diastolic: 84, pulse: 78 } },
      { time: '12:15', values: { systolic: 124, diastolic: 81, pulse: 74 } },
      { time: '18:30', values: { systolic: 121, diastolic: 79, pulse: 71 } },
      { time: '22:20', values: { systolic: 120, diastolic: 78, pulse: 70 } },
    ],
  ],
  MD6280: [
    // 오늘 - 높음
    [
      { time: '08:10', values: { systolic: 131, diastolic: 84, pulse: 78 } },
      { time: '22:15', values: { systolic: 124, diastolic: 79, pulse: 72 } },
    ],
    // 1일 전 - 1건만 측정 / 높음
    [
      { time: '07:50', values: { systolic: 127, diastolic: 82, pulse: 76 } },
    ],
    // 2일 전 - 정상
    [
      { time: '08:20', values: { systolic: 118, diastolic: 76, pulse: 70 } },
      { time: '21:45', values: { systolic: 115, diastolic: 74, pulse: 68 } },
    ],
    // 3일 전 - 높음
    [
      { time: '07:40', values: { systolic: 136, diastolic: 89, pulse: 82 } },
      { time: '13:10', values: { systolic: 130, diastolic: 85, pulse: 78 } },
      { time: '22:05', values: { systolic: 128, diastolic: 83, pulse: 75 } },
    ],
    // 4일 전 - 정상
    [
      { time: '08:05', values: { systolic: 120, diastolic: 79, pulse: 73 } },
    ],
    // 5일 전 - 정상
    [
      { time: '07:55', values: { systolic: 113, diastolic: 73, pulse: 66 } },
      { time: '21:20', values: { systolic: 110, diastolic: 71, pulse: 64 } },
    ],
    // 6일 전 - 높음
    [
      { time: '08:15', values: { systolic: 144, diastolic: 95, pulse: 88 } },
      { time: '22:30', values: { systolic: 138, diastolic: 91, pulse: 84 } },
    ],
  ],
  U8: [
    // 오늘 - 정상
    [
      { time: '06:50', values: { avg_hr: 66, min_hr: 52, max_hr: 98 } },
      { time: '12:30', values: { avg_hr: 81, min_hr: 63, max_hr: 128 } },
      { time: '18:15', values: { avg_hr: 77, min_hr: 60, max_hr: 119 } },
      { time: '22:05', values: { avg_hr: 74, min_hr: 55, max_hr: 110 } },
    ],
    // 1일 전 - 정상
    [
      { time: '07:10', values: { avg_hr: 69, min_hr: 54, max_hr: 101 } },
      { time: '13:00', values: { avg_hr: 84, min_hr: 66, max_hr: 133 } },
      { time: '21:50', values: { avg_hr: 78, min_hr: 58, max_hr: 115 } },
    ],
    // 2일 전 - 정상
    [
      { time: '06:40', values: { avg_hr: 63, min_hr: 49, max_hr: 94 } },
      { time: '12:10', values: { avg_hr: 76, min_hr: 60, max_hr: 121 } },
      { time: '17:45', values: { avg_hr: 72, min_hr: 57, max_hr: 112 } },
      { time: '22:15', values: { avg_hr: 68, min_hr: 52, max_hr: 104 } },
    ],
    // 3일 전 - 높음
    [
      { time: '07:25', values: { avg_hr: 88, min_hr: 68, max_hr: 142 } },
      { time: '13:40', values: { avg_hr: 103, min_hr: 79, max_hr: 158 } },
      { time: '22:00', values: { avg_hr: 105, min_hr: 82, max_hr: 162 } },
    ],
    // 4일 전 - 낮음
    [
      { time: '06:55', values: { avg_hr: 61, min_hr: 47, max_hr: 92 } },
      { time: '12:50', values: { avg_hr: 70, min_hr: 55, max_hr: 108 } },
      { time: '21:40', values: { avg_hr: 58, min_hr: 45, max_hr: 96 } },
    ],
    // 5일 전 - 정상
    [
      { time: '07:05', values: { avg_hr: 72, min_hr: 57, max_hr: 110 } },
      { time: '13:15', values: { avg_hr: 86, min_hr: 67, max_hr: 135 } },
      { time: '18:40', values: { avg_hr: 80, min_hr: 62, max_hr: 124 } },
      { time: '22:10', values: { avg_hr: 76, min_hr: 59, max_hr: 117 } },
    ],
    // 6일 전 - 정상
    [
      { time: '06:35', values: { avg_hr: 67, min_hr: 53, max_hr: 99 } },
      { time: '12:25', values: { avg_hr: 79, min_hr: 61, max_hr: 126 } },
      { time: '22:20', values: { avg_hr: 71, min_hr: 56, max_hr: 107 } },
    ],
  ],
};

export function getMockReadings(deviceId: string, dayOffset?: number) {
  const readingsByDay = MOCK_READINGS_BY_DAY[deviceId];
  return readingsByDay === undefined ? [] : (readingsByDay[getDayIndex(dayOffset)] || []);
}

// 그날의 마지막(최신) 기록 - 기록이 없으면 undefined
export function getLatestReading(deviceId: string, dayOffset?: number) {
  const readings = getMockReadings(deviceId, dayOffset);
  return readings.length === 0 ? undefined : readings[readings.length - 1];
}

// 하루 1회 기록되는 기기의 일자별 측정값
// 기록 목록이 있는 기기(혈압계 / ECG)는 마지막 기록을 그날 값으로 사용 (getMockLatest 참고)
const MOCK_LATEST_BY_DAY: { [deviceId: string]: { [key: string]: number }[] } = {
  T9: [
    { weight_kg: 68.4, body_fat_pct: 21.3, ffm_kg: 53.8, smm_kg: 30.2, tbw_l: 39.4, bmr_kcal: 1520, bmi: 22.6 },
    { weight_kg: 68.6, body_fat_pct: 21.6, ffm_kg: 53.8, smm_kg: 30.2, tbw_l: 39.4, bmr_kcal: 1520, bmi: 22.7 },
    { weight_kg: 68.8, body_fat_pct: 22.0, ffm_kg: 53.7, smm_kg: 30.1, tbw_l: 39.3, bmr_kcal: 1521, bmi: 22.7 },
    { weight_kg: 69.0, body_fat_pct: 22.3, ffm_kg: 53.6, smm_kg: 30.0, tbw_l: 39.3, bmr_kcal: 1522, bmi: 22.8 },
    { weight_kg: 69.4, body_fat_pct: 22.7, ffm_kg: 53.7, smm_kg: 29.9, tbw_l: 39.2, bmr_kcal: 1525, bmi: 22.9 },
    { weight_kg: 69.7, body_fat_pct: 23.0, ffm_kg: 53.7, smm_kg: 29.8, tbw_l: 39.1, bmr_kcal: 1527, bmi: 23.0 },
    { weight_kg: 70.1, body_fat_pct: 23.4, ffm_kg: 53.7, smm_kg: 29.9, tbw_l: 39.2, bmr_kcal: 1530, bmi: 23.2 },
  ],
  IGRIPX: [
    { weight_kg: 68.1, body_fat_pct: 21.8, ffm_kg: 53.3, smm_kg: 29.8, tbw_l: 39.0, bmr_kcal: 1505, bmi: 22.5 },
    { weight_kg: 68.3, body_fat_pct: 22.0, ffm_kg: 53.3, smm_kg: 29.7, tbw_l: 38.9, bmr_kcal: 1504, bmi: 22.6 },
    { weight_kg: 68.5, body_fat_pct: 22.3, ffm_kg: 53.2, smm_kg: 29.6, tbw_l: 38.8, bmr_kcal: 1502, bmi: 22.6 },
    { weight_kg: 68.8, body_fat_pct: 22.6, ffm_kg: 53.3, smm_kg: 29.7, tbw_l: 38.9, bmr_kcal: 1503, bmi: 22.7 },
    { weight_kg: 69.1, body_fat_pct: 22.9, ffm_kg: 53.3, smm_kg: 29.6, tbw_l: 38.8, bmr_kcal: 1502, bmi: 22.8 },
    { weight_kg: 69.5, body_fat_pct: 23.2, ffm_kg: 53.4, smm_kg: 29.7, tbw_l: 38.9, bmr_kcal: 1504, bmi: 23.0 },
    { weight_kg: 69.9, body_fat_pct: 23.6, ffm_kg: 53.4, smm_kg: 29.6, tbw_l: 38.8, bmr_kcal: 1503, bmi: 23.1 },
  ],
  H10: [
    { avg_hr: 72, min_hr: 58, max_hr: 131 },
    { avg_hr: 75, min_hr: 60, max_hr: 138 },
    { avg_hr: 69, min_hr: 55, max_hr: 124 },
    { avg_hr: 81, min_hr: 64, max_hr: 146 },
    { avg_hr: 66, min_hr: 52, max_hr: 118 },
    { avg_hr: 74, min_hr: 59, max_hr: 133 },
    { avg_hr: 58, min_hr: 46, max_hr: 105 },
  ],
  // 혈당기는 시간대별 값(MOCK_BLOOD_BY_DAY) 중 마지막 측정값(취침 전)을 그날 대표값으로 둠
  CSNP: [
    { glucose_mg_dl: 118 },
    { glucose_mg_dl: 112 },
    { glucose_mg_dl: 128 },
    { glucose_mg_dl: 115 },
    { glucose_mg_dl: 98 },
    { glucose_mg_dl: 122 },
    { glucose_mg_dl: 136 },
  ],
};

// 최상단 요약 / 게이지 / 추이 그래프에 쓰는 값 - 기록 목록이 있으면 그날 마지막(최신) 기록 기준
export function getMockLatest(deviceId: string, dayOffset?: number) {
  const latestByDay = MOCK_LATEST_BY_DAY[deviceId];
  const latest = latestByDay === undefined ? {} : (latestByDay[getDayIndex(dayOffset)] || {});
  const latestReading = getLatestReading(deviceId, dayOffset);
  return { ...latest, ...(latestReading === undefined ? {} : latestReading.values) };
}

// 대표 항목의 최근 7일 추이 - 일자별 목데이터에서 계산 (마지막 값이 기준일)
export function getMockTrend(deviceId: string, dayOffset?: number) {
  const primary = getPrimaryMetric(deviceId);
  if (primary === undefined) {
    return [];
  }
  const trend: number[] = [];
  for (let day = 0; day < MOCK_DAY_COUNT; day++) {
    const back = MOCK_DAY_COUNT - 1 - day;   // 0 이 기준일
    const value = getMockLatest(deviceId, (dayOffset || 0) - back)[primary.key];
    if (value === undefined) {
      return [];
    }
    trend.push(value);
  }
  return trend;
}

// 월간 요약 (기기별)
const MOCK_MONTH: { [deviceId: string]: { recordCount: number, valueAvg: number } } = {
  BP170:  { recordCount: 24, valueAvg: 121 },
  MD6280: { recordCount: 18, valueAvg: 126 },
  T9:     { recordCount: 27, valueAvg: 68.8 },
  IGRIPX: { recordCount: 21, valueAvg: 68.5 },
  H10:    { recordCount: 15, valueAvg: 74 },
  CSNP:   { recordCount: 26, valueAvg: 101 },
  U8:     { recordCount: 20, valueAvg: 76 },
};

const MONTH_DAY_COUNT = 31;

export function getMockMonth(deviceId: string) {
  const month = MOCK_MONTH[deviceId] || { recordCount: 0, valueAvg: 0 };
  return {
    recordCount: month.recordCount,
    recordPercent: Math.round(month.recordCount / MONTH_DAY_COUNT * 100),
    valueAvg: month.valueAvg,
  };
}

// ─────────────────────────────────────────────────────────────
// 혈압계 - 수축기 / 이완기 추이 (기존 혈압 기록 화면과 동일한 구성)
// ─────────────────────────────────────────────────────────────

// 이완기(61~80), 수축기(101~120) 기준 상태 / 안내 문구
export function getPressureStatus(high: number, low: number): MetricStatus {
  if (low === 0 && high === 0) {
    return { text: '없음', color: '#777', message: '오늘 혈압을 측정하지 않으셨습니다.' };
  }
  if ((low >= 61 && low <= 80) && (high >= 101 && high <= 120)) {
    return { text: '정상', color: '#279700', message: '오늘 혈압 측정 완료!' };
  }
  if (low > 80 || high > 120) {
    return { text: '높음', color: '#df0000', message: '혈압이 높아요.' };
  }
  return { text: '낮음', color: '#fba400', message: '혈압이 낮아요.' };
}

// 일자별 기록의 마지막(최신) 값을 그날 값으로 사용
export function getMockPressureTrend(deviceId: string, dayCount: number, dayOffset?: number) {
  const systolic: number[] = [];
  const diastolic: number[] = [];
  for (let day = 0; day < dayCount; day++) {
    const back = dayCount - 1 - day;  // 0 이 기준일
    const base = getMockLatest(deviceId, (dayOffset || 0) - back);
    systolic.push(base.systolic + getWeekCycleDelta(back, 2));
    diastolic.push(base.diastolic + getWeekCycleDelta(back, 1));
  }
  return { systolic: systolic, diastolic: diastolic };
}

// 월간 혈압 요약 - 월간 추이에서 직접 계산
export function getMockPressureMonth(deviceId: string) {
  const trend = getMockPressureTrend(deviceId, MONTH_DAY_COUNT);
  const average = (values: number[]) => Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
  const completeCount = trend.systolic.filter((value, index) =>
    value >= 101 && value <= 120 && trend.diastolic[index] >= 61 && trend.diastolic[index] <= 80).length;

  return {
    systolicAvg: average(trend.systolic),
    diastolicAvg: average(trend.diastolic),
    recordCount: getMockMonth(deviceId).recordCount,
    completeCount: completeCount,
    dayCount: MONTH_DAY_COUNT,
    trend: trend,
  };
}


//2026-08-11 수정
// ─────────────────────────────────────────────────────────────
// 혈당기 - 시간대별 측정값 (기존 혈당 기록 화면과 동일한 구성)
// ─────────────────────────────────────────────────────────────

// export const BLOOD_TIME_SLOTS = [
//   { key: 'bloodSugarMorning', label: '아침 식전', color: '#800d8d' },
//   { key: 'bloodSugarMorningAfter', label: '아침 식후', color: 'rgba(188,95,243,0.7)' },
//   { key: 'bloodSugarLunch', label: '점심 식전', color: '#2c6914' },
//   { key: 'bloodSugarLunchAfter', label: '점심 식후', color: '#77c059' },
//   { key: 'bloodSugarDinner', label: '저녁 식전', color: '#d9448d' },
//   { key: 'bloodSugarDinnerAfter', label: '저녁 식후', color: '#ec70ac' },
//   { key: 'bloodSugarSleep', label: '취침 전', color: '#eac222' },
// ];

export const BLOOD_TIME_SLOTS = [
  { key: 'breakfast_pre',  label: '아침 식전', color: '#800d8d' },
  { key: 'breakfast_post', label: '아침 식후', color: 'rgba(188,95,243,0.7)' },
  { key: 'lunch_pre',      label: '점심 식전', color: '#2c6914' },
  { key: 'lunch_post',     label: '점심 식후', color: '#77c059' },
  { key: 'dinner_pre',     label: '저녁 식전', color: '#d9448d' },
  { key: 'dinner_post',    label: '저녁 식후', color: '#ec70ac' },
  { key: 'sleep',          label: '취침 전',   color: '#eac222' },
];


// 일자별 시간대별 혈당 (index 0 이 기준일)
// const MOCK_BLOOD_BY_DAY: { [key: string]: number }[] = [
//   // 오늘 - 정상
//   { bloodSugarMorning: 95, bloodSugarMorningAfter: 132, bloodSugarLunch: 98, bloodSugarLunchAfter: 141, bloodSugarDinner: 102, bloodSugarDinnerAfter: 138, bloodSugarSleep: 118 },
//   // 1일 전 - 정상
//   { bloodSugarMorning: 88, bloodSugarMorningAfter: 126, bloodSugarLunch: 92, bloodSugarLunchAfter: 134, bloodSugarDinner: 96, bloodSugarDinnerAfter: 130, bloodSugarSleep: 112 },
//   // 2일 전 - 높음
//   { bloodSugarMorning: 104, bloodSugarMorningAfter: 148, bloodSugarLunch: 108, bloodSugarLunchAfter: 152, bloodSugarDinner: 112, bloodSugarDinnerAfter: 146, bloodSugarSleep: 128 },
//   // 3일 전 - 정상
//   { bloodSugarMorning: 91, bloodSugarMorningAfter: 129, bloodSugarLunch: 94, bloodSugarLunchAfter: 137, bloodSugarDinner: 99, bloodSugarDinnerAfter: 133, bloodSugarSleep: 115 },
//   // 4일 전 - 낮음
//   { bloodSugarMorning: 66, bloodSugarMorningAfter: 96, bloodSugarLunch: 72, bloodSugarLunchAfter: 105, bloodSugarDinner: 75, bloodSugarDinnerAfter: 112, bloodSugarSleep: 98 },
//   // 5일 전 - 정상
//   { bloodSugarMorning: 99, bloodSugarMorningAfter: 138, bloodSugarLunch: 101, bloodSugarLunchAfter: 144, bloodSugarDinner: 105, bloodSugarDinnerAfter: 141, bloodSugarSleep: 122 },
//   // 6일 전 - 높음
//   { bloodSugarMorning: 112, bloodSugarMorningAfter: 158, bloodSugarLunch: 116, bloodSugarLunchAfter: 163, bloodSugarDinner: 119, bloodSugarDinnerAfter: 155, bloodSugarSleep: 136 },
// ];
const MOCK_BLOOD_BY_DAY: { [key: string]: number }[] = [
  // 오늘 - 정상
  {
    breakfast_pre: 95,
    breakfast_post: 132,
    lunch_pre: 98,
    lunch_post: 141,
    dinner_pre: 102,
    dinner_post: 138,
    sleep: 118
  },

  // 1일 전 - 정상
  {
    breakfast_pre: 88,
    breakfast_post: 126,
    lunch_pre: 92,
    lunch_post: 134,
    dinner_pre: 96,
    dinner_post: 130,
    sleep: 112
  },

  // 2일 전 - 높음
  {
    breakfast_pre: 104,
    breakfast_post: 148,
    lunch_pre: 108,
    lunch_post: 152,
    dinner_pre: 112,
    dinner_post: 146,
    sleep: 128
  },

  // 3일 전 - 정상
  {
    breakfast_pre: 91,
    breakfast_post: 129,
    lunch_pre: 94,
    lunch_post: 137,
    dinner_pre: 99,
    dinner_post: 133,
    sleep: 115
  },

  // 4일 전 - 낮음
  {
    breakfast_pre: 66,
    breakfast_post: 96,
    lunch_pre: 72,
    lunch_post: 105,
    dinner_pre: 75,
    dinner_post: 112,
    sleep: 98
  },

  // 5일 전 - 정상
  {
    breakfast_pre: 99,
    breakfast_post: 138,
    lunch_pre: 101,
    lunch_post: 144,
    dinner_pre: 105,
    dinner_post: 141,
    sleep: 122
  },

  // 6일 전 - 높음
  {
    breakfast_pre: 112,
    breakfast_post: 158,
    lunch_pre: 116,
    lunch_post: 163,
    dinner_pre: 119,
    dinner_post: 155,
    sleep: 136
  },
];


export function getMockBloodLatest(dayOffset: number = 0) {
  return { ...(MOCK_BLOOD_BY_DAY[getDayIndex(dayOffset)] || {}) };
}

// 시간대별 혈당 추이 - 일자별 목데이터에서 계산 (마지막 값이 기준일)
export function getMockBloodTrend(dayCount: number, dayOffset?: number) {
  const trend: { [key: string]: number[] } = {};
  BLOOD_TIME_SLOTS.forEach((slot) => {
    const values: number[] = [];
    for (let day = 0; day < dayCount; day++) {
      const back = dayCount - 1 - day;   // 0 이 기준일
      values.push(getMockBloodLatest((dayOffset || 0) - back)[slot.key] + getWeekCycleDelta(back, 3));
    }
    trend[slot.key] = values;
  });
  return trend;
}

export function getMockBloodMonthDayCount() {
  return MONTH_DAY_COUNT;
}

// 혈당기 카드용 - 가장 최근 시간대 측정값 기준 상태
export function getBloodCardStatus(latestStatus: { status: string, color: string }): MetricStatus {
  if (latestStatus.status === '높음') {
    return { text: '높음', color: latestStatus.color, message: '혈당이 높아요.' };
  }
  if (latestStatus.status === '낮음') {
    return { text: '낮음', color: latestStatus.color, message: '혈당이 낮아요.' };
  }
  if (latestStatus.status === '정상') {
    return { text: '정상', color: latestStatus.color, message: '오늘 혈당 측정 완료!' };
  }
  return { text: '없음', color: '#777', message: '오늘 혈당을 측정하지 않으셨습니다.' };
}

// ─────────────────────────────────────────────────────────────
// ECG - 스트레스 지수 / 부정맥
// ─────────────────────────────────────────────────────────────

export type StressLevel = {
  level: number,   // 1 ~ 5 단계
  label: string,
  color: string,
  min: number,     // 이 값 이상
  max: number,     // 이 값 미만
}

/**
 * 임의로 걸어 둔 상한. 이 값 이상은 5단계 끝으로 본다.
 * (스트레스 지수 자체의 상한은 기기·서버 스펙에 명시돼 있지 않다)
 */
export const STRESS_MAX_SCORE = 1500;

/**
 * 스트레스 지수(SI) 5단계 — 바론 UI 기준.
 *
 * 판정 기준은 AP_009_004 의 `stress_index`(원점수)다. 응답에는 `stress_level` 도
 * 함께 오지만 값이 1~3 뿐이라 5단계로 늘릴 수 없어 화면 표기에는 쓰지 않는다
 * (데이터에는 그대로 남겨 둔다 — healthDeviceApi 의 EcgStress.level).
 *
 *   1단계 매우 안정    0 ≤ SI <   50
 *   2단계 정상        50 ≤ SI <  150
 *   3단계 주의       150 ≤ SI <  500
 *   4단계 높음       500 ≤ SI <  900
 *   5단계 심각       900 ≤ SI < 1500
 */
export const STRESS_LEVELS: StressLevel[] = [
  { level: 1, label: '매우 안정', color: '#3dbb27', min: 0, max: 50 },
  { level: 2, label: '정상', color: '#4a90e2', min: 50, max: 150 },
  { level: 3, label: '주의', color: '#f5cd19', min: 150, max: 500 },
  { level: 4, label: '높음', color: '#fb8c00', min: 500, max: 900 },
  { level: 5, label: '심각', color: '#df0000', min: 900, max: STRESS_MAX_SCORE },
];

/**
 * 지수에 해당하는 단계. 경계는 '이상 ~ 미만' 이다(SI 50 은 2단계).
 * 상한을 넘거나 값이 이상하면 각각 마지막·첫 단계로 본다.
 */
export function getStressLevel(score: number): StressLevel {
  const value = Number(score);
  if (!Number.isFinite(value)) {
    return STRESS_LEVELS[0];
  }
  return STRESS_LEVELS.filter((v) => value < v.max)[0] || STRESS_LEVELS[STRESS_LEVELS.length - 1];
}

/**
 * 균등 분할 막대에서 이 지수가 놓일 위치(0~100%).
 *
 * 단계별 실제 폭이 크게 다르다 — 50 / 100 / 350 / 400 / 600. 그래서 지수를 상한으로
 * 그냥 나누면(SI/1500) 표시가 어긋난다. 예를 들어 SI 100 은 2단계인데 6.7% 지점,
 * 즉 **1단계 칸 안에** 찍힌다.
 *
 * 그래서 '몇 번째 단계인지' + '그 단계 안에서 얼마나 왔는지' 를 균등 칸에 매핑한다.
 * 단계 칸은 화면에서 각 20% 폭이므로, 바늘은 항상 자기 단계 칸 안에 들어온다.
 *   SI 100 → 2단계 · 구간 내 50% → (1 + 0.5) / 5 = 30%
 */
export function getStressPercent(score: number): number {
  const level = getStressLevel(score);
  const index = STRESS_LEVELS.indexOf(level);
  const span = level.max - level.min;
  const value = Number(score);
  const within = span > 0 && Number.isFinite(value)
    ? Math.min(1, Math.max(0, (value - level.min) / span))
    : 0;
  return Math.round(((index + within) / STRESS_LEVELS.length) * 100);
}

// 일자별 스트레스 지수 (그날 마지막 측정 1건만 노출)
const MOCK_STRESS_BY_DAY: { [deviceId: string]: { time: string, score: number }[] } = {
  U8: [
    { time: '22:05', score: 56 },   // 오늘 - 보통
    { time: '21:50', score: 38 },   // 1일 전 - 낮음
    { time: '22:15', score: 24 },   // 2일 전 - 낮음
    { time: '22:00', score: 81 },   // 3일 전 - 매우 높음
    { time: '21:40', score: 17 },   // 4일 전 - 매우 낮음
    { time: '22:10', score: 63 },   // 5일 전 - 높음
    { time: '22:20', score: 45 },   // 6일 전 - 보통
  ],
};

export function getMockStress(deviceId: string, dayOffset?: number) {
  const stressByDay = MOCK_STRESS_BY_DAY[deviceId];
  return stressByDay === undefined ? undefined : stressByDay[getDayIndex(dayOffset)];
}

// 부정맥 기록 - 이상이 확인된 기록만 보관 (정상이면 빈 목록이라 화면에서 숨김)
export type ArrhythmiaRecord = {
  time: string,   // 부정맥이 확인된 시각 (HH:mm)
}

const MOCK_ARRHYTHMIA_BY_DAY: { [deviceId: string]: ArrhythmiaRecord[][] } = {
  U8: [
    [{ time: '12:34' }],                                    // 오늘
    [],                                                     // 1일 전 - 이상 없음
    [],                                                     // 2일 전 - 이상 없음
    [{ time: '09:12' }, { time: '15:47' }, { time: '21:03' }], // 3일 전
    [],                                                     // 4일 전 - 이상 없음
    [{ time: '18:22' }],                                    // 5일 전
    [],                                                     // 6일 전 - 이상 없음
  ],
};

export function getMockArrhythmia(deviceId: string, dayOffset?: number) {
  const arrhythmiaByDay = MOCK_ARRHYTHMIA_BY_DAY[deviceId];
  return arrhythmiaByDay === undefined ? [] : (arrhythmiaByDay[getDayIndex(dayOffset)] || []);
}

// 월간 일별 추이 (대표 항목 기준) - 7일 추이를 반복하며 소폭 변동을 준 값
export function getMockMonthDaily(deviceId: string) {
  const trend = getMockTrend(deviceId);
  if (trend.length === 0) {
    return [];
  }
  const daily: number[] = [];
  for (let day = 0; day < MONTH_DAY_COUNT; day++) {
    const base = trend[day % trend.length];
    const delta = ((day % 5) - 2) * (base > 100 ? 1.5 : 0.3);
    daily.push(Math.round((base + delta) * 10) / 10);
  }
  return daily;
}
